import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// 生成UUID的工具函数
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback UUID生成方法
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// 从localStorage获取用户数据
const getUserDataFromStorage = () => {
  try {
    const userData = localStorage.getItem('7g_user_data');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('获取用户数据失败:', error);
    return null;
  }
};

// 保存用户数据到localStorage
const saveUserDataToStorage = (userData) => {
  try {
    localStorage.setItem('7g_user_data', JSON.stringify(userData));
  } catch (error) {
    console.error('保存用户数据失败:', error);
  }
};

// 异步action：统计播放次数
export const trackPlayCount = createAsyncThunk(
  'user/trackPlayCount',
  async ({ articleId, seriesId }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const playKey = `play_${articleId}`;

      // 直接调用API统计播放次数（让服务器端处理重复检查）
      const response = await axios.post(`/wp-json/hello-world/v1/track-play-count/${articleId}`, {
        user_uuid: state.user.uuid,
        series_id: seriesId
      });

      return { articleId, seriesId, response: response.data };

    } catch (error) {
      console.error('统计播放次数失败:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 异步action：点赞文章
export const likeArticle = createAsyncThunk(
  'user/likeArticle',
  async ({ articleId, seriesId }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      
      // 直接调用API点赞（让服务器端处理重复检查）
      const response = await axios.post(`/wp-json/hello-world/v1/like-article/${articleId}`, {
        user_uuid: state.user.uuid,
        series_id: seriesId
      });

      return { articleId, seriesId, response: response.data };
    } catch (error) {
      console.error('点赞失败:', error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 初始状态
const initialState = (() => {
  const savedData = getUserDataFromStorage();
  
  if (savedData && savedData.uuid) {
    return {
      uuid: savedData.uuid,
      playedVideos: savedData.playedVideos || {},
      likedArticles: savedData.likedArticles || {},
      loading: {
        trackPlay: false,
        like: false
      },
      error: null
    };
  }

  // 如果没有保存的数据，生成新的UUID
  const newUUID = generateUUID();
  const newUserData = {
    uuid: newUUID,
    playedVideos: {},
    likedArticles: {},
    loading: {
      trackPlay: false,
      like: false
    },
    error: null
  };

  saveUserDataToStorage(newUserData);
  return newUserData;
})();

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // 清除错误信息
    clearError: (state) => {
      state.error = null;
    },
    
    // 重置用户数据（用于测试或重置功能）
    resetUserData: (state) => {
      const newUUID = generateUUID();
      state.uuid = newUUID;
      state.playedVideos = {};
      state.likedArticles = {};
      state.error = null;
      
      const newUserData = {
        uuid: newUUID,
        playedVideos: {},
        likedArticles: {}
      };
      saveUserDataToStorage(newUserData);
    },

    // 本地标记播放（用于10秒检测）
    markVideoPlayed: (state, action) => {
      const { articleId } = action.payload;
      const playKey = `play_${articleId}`;
      const timestamp = new Date().toISOString();

      state.playedVideos[playKey] = {
        played: true,
        timestamp,
        synced: false // 标记为未同步到服务器
      };

      // 保存到localStorage
      const userData = {
        uuid: state.uuid,
        playedVideos: state.playedVideos,
        likedArticles: state.likedArticles
      };
      saveUserDataToStorage(userData);
    },

    // 本地标记点赞
    markArticleLiked: (state, action) => {
      const { articleId } = action.payload;
      const likeKey = `like_${articleId}`;
      const timestamp = new Date().toISOString();
      
      state.likedArticles[likeKey] = {
        liked: true,
        timestamp,
        synced: false // 标记为未同步到服务器
      };

      // 保存到localStorage
      const userData = {
        uuid: state.uuid,
        playedVideos: state.playedVideos,
        likedArticles: state.likedArticles
      };
      saveUserDataToStorage(userData);
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理播放统计
      .addCase(trackPlayCount.pending, (state) => {
        state.loading.trackPlay = true;
        state.error = null;
      })
      .addCase(trackPlayCount.fulfilled, (state, action) => {
        state.loading.trackPlay = false;
        
        const { articleId, response } = action.payload;
        const playKey = `play_${articleId}`;
        const timestamp = new Date().toISOString();
        
        // 根据API响应判断是否需要更新本地状态
        if (response && response.success) {
          // 标记本地播放状态
          state.playedVideos[playKey] = {
            played: true,
            timestamp,
            synced: true // 标记为已同步到服务器
          };

          // 保存到localStorage
          const userData = {
            uuid: state.uuid,
            playedVideos: state.playedVideos,
            likedArticles: state.likedArticles
          };
          saveUserDataToStorage(userData);
        }
      })
      .addCase(trackPlayCount.rejected, (state, action) => {
        state.loading.trackPlay = false;
        state.error = action.payload;
      })
      
      // 处理点赞
      .addCase(likeArticle.pending, (state) => {
        state.loading.like = true;
        state.error = null;
      })
      .addCase(likeArticle.fulfilled, (state, action) => {
        state.loading.like = false;
        
        const { articleId, response } = action.payload;
        const likeKey = `like_${articleId}`;
        const timestamp = new Date().toISOString();
        
        // 根据API响应判断是否需要更新本地状态
        if (response && response.success) {
          // 标记本地点赞状态
          state.likedArticles[likeKey] = {
            liked: true,
            timestamp,
            synced: true // 标记为已同步到服务器
          };

          // 保存到localStorage
          const userData = {
            uuid: state.uuid,
            playedVideos: state.playedVideos,
            likedArticles: state.likedArticles
          };
          saveUserDataToStorage(userData);
        }
      })
      .addCase(likeArticle.rejected, (state, action) => {
        state.loading.like = false;
        state.error = action.payload;
      });
  }
});

export const { 
  clearError, 
  resetUserData, 
  markVideoPlayed, 
  markArticleLiked 
} = userSlice.actions;

// Selectors
export const selectUser = (state) => state.user;
export const selectUserUUID = (state) => state.user.uuid;
export const selectIsVideoPlayed = (state, articleId) => {
  const playKey = `play_${articleId}`;
  return !!state.user.playedVideos[playKey];
};
export const selectIsArticleLiked = (state, articleId) => {
  const likeKey = `like_${articleId}`;
  return !!state.user.likedArticles[likeKey];
};
export const selectUserLoading = (state) => state.user.loading;
export const selectUserError = (state) => state.user.error;

export default userSlice.reducer; 